import initHelpHeroSdk from 'helphero'
import brand from '@/composables/useBrand'
import { isEmbedLatched } from '@/utils/embed'
import { getPlanByPriceId } from '@/utils/payments'
import { runAiWrites } from '@/utils/aiAuthorship'

let helpHero = null
let routerRef = null
let storeRef = null
let tourContext = null
let identifiedUserId = null
let pendingProps = {}
let openAiActions = 0
let uncountedAiActions = 0

const isEnabled = () => Boolean(brand.analytics?.helpHeroAppId) &&
  !window.Cypress &&
  !isEmbedLatched() &&
  !window.location.pathname.startsWith('/embed/')

const toDate = (value) => {
  if (!value) {
    return undefined
  }

  const date = new Date(value)

  return Number.isNaN(date.getTime()) ? undefined : date
}

const compact = (props) => Object.fromEntries(
  Object.entries(props).filter(([, value]) => value !== undefined && value !== null)
)

const hasResult = (response) => (Array.isArray(response) ? response.length > 0 : Boolean(response))

// HelpHero hands tours an absolute URL; a full page load would drop the SPA state mid-tour.
const navigate = (url) => {
  const target = new URL(url, window.location.origin)

  if (!routerRef || target.origin !== window.location.origin) {
    window.location.assign(url)

    return
  }

  routerRef.push(target.pathname + target.search + target.hash)
}

const findMembership = (state, projectId) => {
  const memberships = state.projectMemberships.projectMemberships || []

  return memberships.find((membership) => membership.project?.id === projectId) || null
}

const activeOrganisation = (state) => {
  const organisation = findMembership(state, state.projectMemberships.activeProjectId)?.project?.organisation

  if (organisation) {
    return organisation
  }

  const owned = (state.organisations.organisations || []).find((item) => item.userId === state.userId)
  const memberships = state.projectMemberships.projectMemberships || []

  return owned || memberships[0]?.project?.organisation || null
}

const identityProps = (user) => ({
  name: [user.firstname, user.lastname].filter(Boolean).join(' '),
  role: user.job || undefined
})

const subscriptionProps = (payments) => {
  const details = payments.activeSubscription?.subscriptionDetails
  const status = details?.status
  let plan = 'none'

  if (payments.isEnterpriseUser) {
    plan = 'enterprise'
  } else if (details?.price?.id) {
    plan = getPlanByPriceId(details.price.id)?.value || details.price.productName?.toLowerCase() || 'unknown'
  }

  return {
    plan,
    isPaying: status === 'active',
    trialEndsAt: status === 'trialing' ? toDate(details.nextBillingDate) : undefined,
    seatCount: details?.quantity ?? 0
  }
}

const integrationProps = (state) => {
  const user = state.user.user
  const memberships = state.projectMemberships.projectMemberships || []
  const jiraConnected = memberships.some((membership) => Boolean(membership.project?.jiraForgeData?.projectId))
  const connected = [
    jiraConnected && 'jira',
    state.github.githubAccounts?.length > 0 && 'github',
    user?.hasMcpApiKey && 'mcp'
  ].filter(Boolean)

  return {
    jiraConnected,
    connectedIntegrations: connected.join(',')
  }
}

export const buildHelpHeroProps = (state) => {
  const user = state.user.user
  const memberships = state.projectMemberships.projectMemberships || []
  const organisation = activeOrganisation(state)
  const workflowCount = memberships.reduce((sum, membership) => sum + (membership.project?.workflows?.length || 0), 0)
  const organisationCount = state.organisations.organisations?.length || 0

  return compact({
    ...identityProps(user),
    hostname: window.location.hostname,
    orgId: organisation?.id,
    orgName: organisation?.name,
    orgCreatedAt: toDate(organisation?.createdAt),
    isOrgCreator: !!organisation && organisation.userId === state.userId,
    ...subscriptionProps(state.payments),
    createdAt: toDate(user.registrationDate),
    lastActiveAt: toDate(user.lastLogin),
    loginCount: user.loginCounter,
    workflowCount,
    completedOnboarding: organisationCount > 0 && memberships.length > 0 && workflowCount > 0,
    ...integrationProps(state)
  })
}

// Usage flags live in HelpHero only. Filling just the missing keys keeps counts across logins.
const fillUsageDefaults = (current) => {
  const props = current || {}

  return {
    aiGenerationCount: Number(props.aiGenerationCount) || 0,
    hasGeneratedUserStories: props.hasGeneratedUserStories === true,
    hasGeneratedDataModel: props.hasGeneratedDataModel === true,
    hasInvitedTeammate: props.hasInvitedTeammate === true,
    hasExported: props.hasExported === true
  }
}

const bumpAiGenerationCount = (current) => ({
  aiGenerationCount: (Number(current?.aiGenerationCount) || 0) + 1
})

const TOUR_OUTCOMES = {
  tour_completed: 'completed',
  tour_cancelled: 'cancelled',
  tour_interrupted: 'interrupted',
  error: 'error'
}

const tourListeners = []

export const onTourChange = (listener) => {
  tourListeners.push(listener)
}

const notifyTourListeners = (change) => {
  tourListeners.forEach((listener) => listener(change))
}

// The workflow on screen. From the page's route first: a tour that starts as the page loads
// fires before the workflow has reached the store.
const openWorkflowId = () => {
  const route = routerRef?.currentRoute?.value

  return (route?.path?.startsWith('/workflow/') && route.params?.id) || storeRef?.state?.workflow?.workflow?.id
}

// A tour resumed after a page reload may only report tour_advanced, so any step counts as active.
const onTourEvent = (event) => {
  const outcome = TOUR_OUTCOMES[event?.kind]

  if (outcome) {
    if (tourContext) {
      notifyTourListeners({ ended: true, workflowId: tourContext.tourWorkflowId, outcome })
    }
    tourContext = null

    return
  }

  if (!tourContext && (event?.kind === 'tour_started' || event?.kind === 'tour_advanced')) {
    tourContext = { context: 'onboarding', tourWorkflowId: openWorkflowId() }
    notifyTourListeners({ started: true, workflowId: tourContext.tourWorkflowId })
  }
}

export const getTourContext = () => tourContext

export const resetHelpHero = () => {
  identifiedUserId = null
  pendingProps = {}
  tourContext = null

  if (helpHero) {
    helpHero.reset()
  }
}

const onMutation = (mutation) => {
  if (mutation.type === 'logout') {
    resetHelpHero()
  }
}

export const initHelpHero = ({ store, router }) => {
  if (!isEnabled()) {
    return
  }

  routerRef = router
  storeRef = store
  helpHero = initHelpHeroSdk(brand.analytics.helpHeroAppId)
  helpHero.setOptions({ navigate })
  helpHero.on(onTourEvent)
  store.subscribe(onMutation)
}

export const identifyHelpHeroUser = (state) => {
  const user = state.user.user

  if (!helpHero || !user?.id || isEmbedLatched()) {
    return
  }

  helpHero.identify(`${window.location.hostname}:${user.id}`, buildHelpHeroProps(state))
  helpHero.update(fillUsageDefaults)
  identifiedUserId = user.id

  if (Object.keys(pendingProps).length > 0) {
    helpHero.update(pendingProps)
    pendingProps = {}
  }
}

// Updates that arrive before identify (a workflow opened straight from a link) are sent right after it.
export const updateHelpHero = (props) => {
  if (!helpHero || isEmbedLatched()) {
    return
  }

  const payload = compact(props)

  if (Object.keys(payload).length === 0) {
    return
  }

  if (!identifiedUserId) {
    pendingProps = { ...pendingProps, ...payload }

    return
  }

  helpHero.update(payload)
}

export const trackAiGeneration = () => {
  if (!helpHero || !identifiedUserId) {
    return
  }

  if (openAiActions > 0) {
    if (uncountedAiActions === 0) {
      return
    }

    uncountedAiActions--
  }

  helpHero.update(bumpAiGenerationCount)
}

// One AI action for HelpHero's count. Its saves are the AI's (runAiWrites) unless writesAsAi is
// false: an action that also waits on the model, while the person can still edit by hand, then
// wraps only the part that applies the AI's changes in runAiWrites itself.
export const runAiGeneration = async (action, { writesAsAi = true } = {}) => {
  openAiActions++
  uncountedAiActions++

  try {
    return await (writesAsAi ? runAiWrites(action) : action())
  } finally {
    openAiActions--
    uncountedAiActions = Math.min(uncountedAiActions, openAiActions)
  }
}

export const trackCardsGenerated = (response) => {
  if (hasResult(response)) {
    updateHelpHero({ hasGeneratedUserStories: true })
  }
}

export const trackEntitiesGenerated = (response) => {
  if (hasResult(response)) {
    updateHelpHero({ hasGeneratedDataModel: true })
  }
}

export const trackExport = (format) => {
  updateHelpHero({ hasExported: true, lastExportFormat: format })
}

export const syncHelpHeroWorkflow = (state) => {
  const workflow = state.workflow.workflow
  const activeProjectId = state.projectMemberships.activeProjectId

  if (!workflow?.id || (activeProjectId && workflow.projectId !== activeProjectId)) {
    return
  }

  const eventCount = workflow.domainEvents?.length || 0
  const membership = findMembership(state, workflow.projectId)

  updateHelpHero({
    currentWorkflowId: workflow.id,
    isWorkflowOwner: membership?.project?.organisation?.userId === state.userId,
    workflowIsEmpty: eventCount === 0,
    workflowEventCount: eventCount
  })
}

// Login and session restore identify; later user refreshes (profile save, GitHub connect) only update.
export const syncHelpHeroUser = (state) => {
  const user = state.user.user

  if (identifiedUserId && identifiedUserId === user?.id) {
    updateHelpHero({ ...identityProps(user), ...integrationProps(state) })

    return
  }

  identifyHelpHeroUser(state)
  syncHelpHeroWorkflow(state)
}

export const syncHelpHeroIntegrations = (state) => {
  updateHelpHero(integrationProps(state))
}

export const syncHelpHeroWorkspace = (state) => {
  if (!identifiedUserId) {
    return
  }

  identifyHelpHeroUser(state)
  syncHelpHeroWorkflow(state)

  const ownsProject = findMembership(state, state.projectMemberships.activeProjectId)?.project?.organisation?.userId === state.userId

  if (ownsProject && (state.teamMembers.teamMembers?.length || 0) > 1) {
    updateHelpHero({ hasInvitedTeammate: true })
  }
}
